import { useState } from "react";
import { Alert, Box, Button, IconButton } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { differenceInCalendarDays, format } from "date-fns";

// ----------------------------------------------------------------------

const THRESHOLD_DAYS = 7;
const DISMISS_KEY = "renewalBannerDismissed";

type Props = {
  planType: "free" | "paid";
  endDate?: string;
  onRenew: () => void;
};

/** Aviso dispensável no topo do dashboard quando o plano está perto de vencer. */
export default function RenewalBanner({ planType, endDate, onRenew }: Props) {
  const todayKey = format(new Date(), "yyyy-MM-dd");
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === todayKey);

  if (planType === "free" || !endDate) return null;
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return null;

  const daysLeft = differenceInCalendarDays(end, new Date());
  if (daysLeft < 0 || daysLeft > THRESHOLD_DAYS || dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, todayKey);
    setDismissed(true);
  };

  const label =
    daysLeft === 0 ? "Seu plano vence hoje" : daysLeft === 1 ? "Seu plano vence amanhã" : `Seu plano vence em ${daysLeft} dias`;

  return (
    <Alert
      severity="warning"
      variant="outlined"
      sx={{
        mb: 2.5,
        borderRadius: "14px",
        alignItems: "center",
        "& .MuiAlert-message": { flex: 1, minWidth: 0 },
        "& .MuiAlert-action": { m: 0, pl: 1 },
        "@media (max-width: 360px)": {
          alignItems: "flex-start",
          flexDirection: "column",
          "& .MuiAlert-message": { width: "100%" },
          "& .MuiAlert-action": { alignSelf: "flex-end", pt: 1, pl: 0 },
        },
      }}
      action={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button color="warning" size="small" variant="contained" onClick={onRenew} sx={{ whiteSpace: "nowrap" }}>
            Renovar plano
          </Button>
          <IconButton size="small" aria-label="Dispensar aviso" onClick={handleDismiss}>
            <CloseRounded fontSize="small" />
          </IconButton>
        </Box>
      }
    >
      {label} — renove para não perder o acesso.
    </Alert>
  );
}